import { useCallback, useEffect, useState } from "react";
import { Plus, Pencil, Trash2, MessageSquareText, Tags, UserCog, RefreshCw, History, AlarmClock, Plane, Package } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ROLE_LABEL } from "@/lib/roles";
import { AUDIT } from "@/constants/testIds";

const PAGE = 50;
const FILTERS = [
  { id: "all", label: "Semua" },
  { id: "template", label: "Template" },
  { id: "category", label: "Kategori" },
  { id: "user", label: "Akun" },
  { id: "booking", label: "Booking" },
  { id: "product", label: "Produk" },
  { id: "seat", label: "Status Seat" },
];
const ACTION = {
  create: { verb: "menambah", icon: Plus, tone: "bg-emerald-50 text-emerald-700" },
  update: { verb: "mengubah", icon: Pencil, tone: "bg-sky-50 text-sky-700" },
  delete: { verb: "menghapus", icon: Trash2, tone: "bg-red-50 text-red-700" },
};
const ENTITY = { template: { label: "template", icon: MessageSquareText }, category: { label: "kategori", icon: Tags }, user: { label: "akun", icon: UserCog }, booking: { label: "booking", icon: AlarmClock }, product: { label: "produk", icon: Package }, seat: { label: "status seat", icon: Plane } };

const fmtTime = (s) => new Date(s.endsWith("Z") || s.includes("+") ? s : `${s}Z`).toLocaleString("id-ID", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });

const LogRow = ({ log }) => {
  const a = ACTION[log.action];
  const E = ENTITY[log.entity];
  return (
    <li className="flex gap-4 px-5 py-4" data-testid={AUDIT.row}>
      <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${a.tone}`}><a.icon className="h-4 w-4" /></div>
      <div className="min-w-0 flex-1">
        <p className="text-sm text-slate-700 leading-relaxed">
          <span className="font-semibold text-slate-900">{log.actor_name}</span>
          <span className="text-xs text-slate-400"> ({ROLE_LABEL[log.actor_role] || log.actor_role})</span>{" "}
          {a.verb} {E.label} <span className="font-medium text-slate-900">"{log.entity_name}"</span>
        </p>
        {log.detail && <p className="mt-0.5 text-xs text-slate-500">{log.detail}</p>}
      </div>
      <div className="flex shrink-0 flex-col items-end gap-1">
        <span className="text-xs text-slate-400 tabular-nums whitespace-nowrap" data-testid={AUDIT.time}>{fmtTime(log.created_at)}</span>
        <E.icon className="h-3.5 w-3.5 text-slate-300" />
      </div>
    </li>
  );
};

export default function AuditLog() {
  const [entity, setEntity] = useState("all");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (skip = 0) => {
    setLoading(true);
    try {
      const { data } = await api.get("/audit-logs", { params: { entity: entity === "all" ? undefined : entity, skip, limit: PAGE } });
      setItems((prev) => (skip ? [...prev, ...data.items] : data.items));
      setTotal(data.total);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, [entity]);

  useEffect(() => {
    load(0);
  }, [load]);

  return (
    <div className="max-w-4xl space-y-8">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between fade-up">
        <div>
          <p className="overline-label">Pengaturan</p>
          <h1 className="font-heading mt-2 text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">Riwayat Perubahan</h1>
          <p className="mt-2 text-sm text-slate-500">Catatan siapa yang menambah, mengubah, atau menghapus template, kategori, akun, dan booking.</p>
        </div>
        <button onClick={() => load(0)} className="flex items-center gap-2 self-start rounded-full border border-slate-200 bg-white px-4 py-2 text-sm text-slate-600 hover:border-slate-300 hover:text-brand transition-colors sm:self-auto" data-testid={AUDIT.refresh}>
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Segarkan
        </button>
      </div>
      <div className="flex gap-2 overflow-x-auto no-scrollbar">
        {FILTERS.map((f) => (
          <button key={f.id} onClick={() => setEntity(f.id)} data-testid={`${AUDIT.filter}-${f.id}`}
            className={`shrink-0 rounded-full border px-4 py-1.5 text-sm transition-colors ${entity === f.id ? "border-brand bg-brand text-white" : "border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:text-slate-900"}`}>
            {f.label}
          </button>
        ))}
      </div>
      <p className="text-xs text-slate-500" data-testid={AUDIT.count}>{total} catatan</p>
      <ul className="divide-y divide-slate-200 rounded-lg border border-slate-200 bg-white">
        {items.length === 0 && !loading && (
          <li className="flex flex-col items-center px-5 py-14 text-center text-sm text-slate-500" data-testid={AUDIT.empty}>
            <History className="mb-3 h-8 w-8 text-slate-300" />Belum ada catatan perubahan.
          </li>
        )}
        {items.map((l) => <LogRow key={l.id} log={l} />)}
      </ul>
      {items.length < total && (
        <Button variant="outline" onClick={() => load(items.length)} disabled={loading} className="rounded-full" data-testid={AUDIT.loadMore}>Muat lebih banyak</Button>
      )}
    </div>
  );
}
